import { useEffect, useRef } from 'react';
import { useMap } from 'react-map-gl';
import { FeatureCollection, Point } from 'geojson';
import { useMapContext } from 'src/@realtime/contexts/map';
import * as turf from '@turf/turf';
import { useConfigSelectors } from 'src/app/reducers/configReducer';
import mapboxgl from 'mapbox-gl';
import sassVars from 'src/styles/vars.module.scss';
import { calculateDistance } from 'src/@realtime/utils';
import { doesLayerExist } from 'src/utils/mapHelpers/mapLayerHelpers';

export const MeasureLayer = (): JSX.Element | null => {
  const { current: mapRef } = useMap();
  const { state } = useMapContext();
  const startPointRef = useRef<[number, number] | null>(null);
  const endPointRef = useRef<[number, number] | null>(null);
  const configSelectors = useConfigSelectors();
  const selectedDistanceUnits = configSelectors.getDistanceUnitsProfile() as
    | 'US Customary'
    | 'ICAO Metric'
    | 'ICAO Alternative'
    | 'Local System';

  const sourceLineId = 'measure-line';
  const sourcePointsId = 'measure-points';
  const layerLineId = 'measure-line-layer';
  const layerPointsId = 'measure-points-layer';

  const updateMapFeatures = (map: mapboxgl.Map) => {
    if (!startPointRef.current || !endPointRef.current) {
      return;
    }

    const startPoint = startPointRef.current;
    const endPoint = endPointRef.current;
    const distance = calculateDistance(startPoint, endPoint, selectedDistanceUnits);

    // The dotted line
    const line = turf.lineString([startPoint, endPoint]);

    // The handle circles
    const features: FeatureCollection<Point> = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: startPoint },
          properties: { type: 'start' },
        },
        {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: endPoint },
          properties: {
            type: 'end',
            distance,
          },
        },
      ],
    };

    (map.getSource(sourceLineId) as mapboxgl.GeoJSONSource)?.setData(line);
    (map.getSource(sourcePointsId) as mapboxgl.GeoJSONSource)?.setData(features);

    const existingLabel = document.querySelector('.ruler-label-container');
    if (existingLabel) {
      existingLabel.remove();
    }

    // Add a custom HTML label near the end point, the MapBox  type: 'symbol' doesn't look good at all.
    const markerElement = createDistanceLabel(distance);
    new mapboxgl.Marker({
      element: markerElement,
      anchor: 'bottom',
    })
      .setLngLat(endPoint)
      .addTo(map);
  };

  const addMeasureSourcesAndLayers = (map: mapboxgl.Map) => {
    if (!map.getSource(sourceLineId)) {
      map.addSource(sourceLineId, {
        type: 'geojson',
        data: turf.lineString([[0, 0], [0, 0]]),
      });
    }

    if (!map.getLayer(layerLineId)) {
      map.addLayer({
        id: layerLineId,
        type: 'line',
        source: sourceLineId,
        paint: {
          'line-color': sassVars.brand01,
          'line-width': 2,
          'line-dasharray': [2, 1],
        },
      });
    }

    if (!map.getSource(sourcePointsId)) {
      map.addSource(sourcePointsId, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }

    if (!map.getLayer(layerPointsId)) {
      map.addLayer({
        id: layerPointsId,
        type: 'circle',
        source: sourcePointsId,
        paint: {
          'circle-radius': 5,
          'circle-color': sassVars.white,
          'circle-stroke-width': 2,
          'circle-stroke-color': sassVars.brand01,
        },
      });
    }

    updateMapFeatures(map);
  };

  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map) {
      return;
    }

    const initializeMeasure = () => {
      const center = map.getCenter();
      startPointRef.current = [center.lng - 0.2051, center.lat];
      endPointRef.current = [center.lng + 0.2, center.lat];
      addMeasureSourcesAndLayers(map);
    };

    const onStyleLoad = () => {
      // console.log('[MeasureLayer] style.load triggered');

      if (state.isMeasuring) {
        // console.log('[MeasureLayer] Re-adding measure layers after style change');
        addMeasureSourcesAndLayers(map);
      }
    };

    const onMouseDown = (e: mapboxgl.MapMouseEvent) => {
      if (!doesLayerExist(map, layerPointsId)) {
        return;
      }

      const features = map.queryRenderedFeatures(e.point, { layers: [layerPointsId] });
      if (!features.length) {
        return;
      }

      const type = features[0].properties?.type as 'start' | 'end';

      // Disable map dragging during handle drag
      map.dragPan.disable();
      const onMouseMove = (moveEvent: mapboxgl.MapMouseEvent) => {
        const lngLat: [number, number] = [moveEvent.lngLat.lng, moveEvent.lngLat.lat];
        if (type === 'start') {
          startPointRef.current = lngLat;
        }
        else if (type === 'end') {
          endPointRef.current = lngLat;
        }

        updateMapFeatures(map);
      };

      map.on('mousemove', onMouseMove);
      map.once('mouseup', () => {
        map.off('mousemove', onMouseMove);
        map.dragPan.enable();
      });
    };

    map.on('style.load', onStyleLoad);

    if (state.isMeasuring) {
      initializeMeasure();
      map.on('mousedown', onMouseDown);
    } else {
      const existingLabel = document.querySelector('.ruler-label-container');
      if (existingLabel) {
        existingLabel.remove();
      }

      startPointRef.current = null;
      endPointRef.current = null;

      if (map.getLayer(layerLineId)) {
        map.removeLayer(layerLineId);
      }
      if (map.getSource(sourceLineId)) {
        map.removeSource(sourceLineId);
      }
      if (map.getLayer(layerPointsId)) {
        map.removeLayer(layerPointsId);
      }
      if (map.getSource(sourcePointsId)) {
        map.removeSource(sourcePointsId);
      }

      map.off('mousedown', onMouseDown);
      map.off('style.load', onStyleLoad);
    }

    return () => {
      map.off('mousedown', onMouseDown);
      map.off('style.load', onStyleLoad);
    };
  }, [state.isMeasuring]);

  return null;
};

const createDistanceLabel = (distance: string) => {
  const containerDiv = document.createElement('div');
  containerDiv.className = 'ruler-end-container';
  containerDiv.style.left = '10px'; // Offset from the end point
  containerDiv.style.whiteSpace = 'nowrap';

  const labelDiv = document.createElement('div');
  labelDiv.className = 'ruler-label-container';
  labelDiv.textContent = distance;
  labelDiv.style.fontWeight = 'bold';

  containerDiv.appendChild(labelDiv);
  return containerDiv;
};